import React, { useState, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { redeemGroupCode, redeemGroupCodeReset, validateGroupCode, validateGroupCodeReset } from 'redux/GroupOrders/GroupOrdersActions';
import { listPaymentMethods } from 'redux/Billing/BillingActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';
import IndividualCodePaymentForm from './IndividualCodePaymentForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const RedeemGroupCode = (props) => {
  const dispatch = useDispatch();
  const [code, setCode] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const timerRef = useRef(null);

  const validationData = props.validateGroupCode.data;
  const isIndividual = validationData?.valid && validationData?.paymentType === 'individual';
  const isCoordinatorCode = validationData?.valid && !isIndividual;
  const isValid = validationData?.valid;
  const isInvalid = code.trim().length >= 6 && validationData && !validationData.valid;

  // Auto-validate code as user types
  const handleCodeChange = (e) => {
    const val = e.target.value.toUpperCase();
    setCode(val);
    dispatch(validateGroupCodeReset());

    if (timerRef.current) clearTimeout(timerRef.current);

    if (val.trim().length >= 6) {
      timerRef.current = setTimeout(() => {
        dispatch(validateGroupCode(val.trim()));
      }, 500);
    }
  };

  // Handle "Redeem Code" button click
  const handleRedeemClick = () => {
    if (isCoordinatorCode) {
      // Coordinator code — redeem directly
      dispatch(redeemGroupCode(code.trim()));
    } else if (isIndividual) {
      // Individual code — open payment modal
      dispatch(listPaymentMethods());
      setPaymentModalOpen(true);
    }
  };

  // Close payment modal
  const closePaymentModal = () => {
    setPaymentModalOpen(false);
  };

  // Handle coordinator redeem result
  useEffect(() => {
    if (props.redeemGroupCode.success) {
      Swal.fire({
        title: 'Code Redeemed!',
        text: `Your subscription has been activated for ${props.redeemGroupCode.data.months} months.`,
        icon: 'success',
      });
      setCode('');
      dispatch(redeemGroupCodeReset());
      dispatch(validateGroupCodeReset());
      dispatch(getUserData({ onLogin: true }));
    }

    if (props.redeemGroupCode.error) {
      Swal.fire(
        'Error',
        props.redeemGroupCode.error.error || 'Failed to redeem code',
        'error'
      );
      dispatch(redeemGroupCodeReset());
    }
  }, [props.redeemGroupCode]);

  // Handle individual payment success
  const handlePaymentSuccess = (data) => {
    setPaymentModalOpen(false);
    if (data.activated) {
      Swal.fire({
        title: 'Subscription Activated!',
        text: data.message,
        icon: 'success',
      });
      setCode('');
      dispatch(validateGroupCodeReset());
      dispatch(getUserData({ onLogin: true }));
    } else {
      Swal.fire({
        title: 'Payment Confirmed!',
        text: data.message,
        icon: 'success',
      });
      setCode('');
      dispatch(validateGroupCodeReset());
    }
  };

  const savedCards = props.paymentMethods.paymentMethods || [];

  return (
    <>
      {/* Inline section */}
      <div>
        <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
          <i className="fas fa-ticket-alt mr-2" />
          Enter the code provided by your residency program coordinator.
        </p>
        <InputGroup>
          <Input
            placeholder="Enter code (e.g. GRP-ABC123)"
            value={code}
            onChange={handleCodeChange}
            maxLength={10}
            style={{ fontSize: '1.05rem', letterSpacing: '1px' }}
          />
          <InputGroupAddon addonType="append">
            {props.validateGroupCode.loading ? (
              <Button color="secondary" disabled>
                <Spinner size="sm" />
              </Button>
            ) : (
              <Button
                color={isValid ? 'success' : 'default'}
                disabled={!isValid || props.redeemGroupCode.loading}
                onClick={handleRedeemClick}
              >
                {props.redeemGroupCode.loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className={`fas ${isValid ? 'fa-check-circle' : 'fa-ticket-alt'} mr-1`} />
                    Redeem Code
                  </>
                )}
              </Button>
            )}
          </InputGroupAddon>
        </InputGroup>

        {/* Validation feedback */}
        {isInvalid && (
          <small className="text-danger mt-1 d-block">
            <i className="fas fa-times-circle mr-1" />{validationData.error || 'Invalid code'}
          </small>
        )}
        {isCoordinatorCode && (
          <small className="text-success mt-1 d-block">
            <i className="fas fa-check-circle mr-1" />Valid code — {validationData.programName}
          </small>
        )}
        {isIndividual && (
          <small className="text-info mt-1 d-block">
            <i className="fas fa-credit-card mr-1" />
            This code requires individual payment — ${validationData.priceAmount}
          </small>
        )}
      </div>

      {/* Payment modal for individual codes */}
      <Modal isOpen={paymentModalOpen} toggle={closePaymentModal} size="lg">
        <ModalHeader toggle={closePaymentModal}>
          <i className="fas fa-credit-card text-primary mr-2" />
          Pay for Group Access Code
        </ModalHeader>
        <ModalBody>
          {isIndividual && (
            <div className="mb-3 p-3 bg-secondary rounded">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{validationData.programName}</strong>
                  <br />
                  <small className="text-muted">Code: {code}</small>
                </div>
                <h3 className="mb-0 text-primary">${validationData.priceAmount}</h3>
              </div>
            </div>
          )}
          {paymentModalOpen && isIndividual && (
            <Elements
              stripe={stripePromise}
              options={{
                mode: 'payment',
                amount: Math.round(validationData.priceAmount * 100),
                currency: 'usd',
                payment_method_types: ['card', 'link'],
                setup_future_usage: 'off_session',
              }}
            >
              <IndividualCodePaymentForm
                code={code.trim()}
                priceAmount={validationData.priceAmount}
                planId={validationData.planId}
                savedCards={savedCards}
                onSuccess={handlePaymentSuccess}
              />
            </Elements>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  redeemGroupCode: state.redeemGroupCode,
  validateGroupCode: state.validateGroupCode,
  paymentMethods: state.paymentMethods,
});

export default connect(mapStateToProps)(RedeemGroupCode);
